import { z } from "zod";
import { CA_PROVINCES, US_STATES } from "@/lib/geo/regions";

export const signInSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

export const signUpSchema = z
  .object({
    email: z.string().email("Please enter a valid email address"),
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        "Password must contain at least one uppercase letter, one lowercase letter, and one number"
      ),
    confirmPassword: z.string(),
    fullName: z
      .string()
      .min(2, "Full name must be at least 2 characters")
      .max(100, "Full name must be less than 100 characters"),
    clinicName: z
      .string()
      .min(2, "Clinic name must be at least 2 characters")
      .max(100, "Clinic name must be less than 100 characters"),
    phone: z.string().optional(),
    streetAddress: z
      .string()
      .min(2, "Street address is required")
      .max(200, "Street address must be less than 200 characters"),
    city: z
      .string()
      .min(2, "City is required")
      .max(100, "City must be less than 100 characters"),
    country: z.enum(["CA", "US"]),
    province: z.string().min(1, "Province / State is required"),
    postalCode: z.string().min(1, "Postal / ZIP code is required"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  })
  .superRefine((data, ctx) => refineCountryPostalCode(data, ctx));

const clinicProfileFields = {
  clinicName: z
    .string()
    .min(2, "Clinic name must be at least 2 characters")
    .max(100, "Clinic name must be less than 100 characters"),
  streetAddress: z
    .string()
    .min(2, "Street address is required")
    .max(200, "Street address must be less than 200 characters"),
  city: z
    .string()
    .min(2, "City is required")
    .max(100, "City must be less than 100 characters"),
  country: z.enum(["CA", "US"]),
  province: z.string().min(1, "Province / State is required"),
  postalCode: z.string().min(1, "Postal / ZIP code is required"),
};

export const clinicProfileSchema = z
  .object(clinicProfileFields)
  .superRefine((data, ctx) => refineCountryPostalCode(data, ctx));

function refineCountryPostalCode(
  data: { country: "CA" | "US"; postalCode: string; province: string },
  ctx: z.RefinementCtx
) {
  const validRegions = new Set<string>(
    (data.country === "US" ? US_STATES : CA_PROVINCES).map((region) => region.value)
  );
  if (data.province && !validRegions.has(data.province)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: data.country === "US" ? "Select a US state" : "Select a Canadian province",
      path: ["province"],
    });
  }
  if (data.country === "CA" && !/^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/.test(data.postalCode)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Please enter a valid Canadian postal code (e.g. A1A 1A1)",
      path: ["postalCode"],
    });
  }
  if (data.country === "US" && !/^\d{5}(-\d{4})?$/.test(data.postalCode)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Please enter a valid US ZIP code (e.g. 90210)",
      path: ["postalCode"],
    });
  }
}

export function signupErrorMessage(errorMessage: string): string {
  const lower = errorMessage.toLowerCase();
  if (
    lower.includes("already registered") ||
    lower.includes("already been registered") ||
    lower.includes("user already exists") ||
    lower.includes("already exists")
  ) {
    return "An account with this email already exists. Sign in, or use Forgot password if you never confirmed your email.";
  }
  return errorMessage;
}

export function isDuplicateSignup(user: { identities?: unknown[] | null } | null): boolean {
  return !!user && (user.identities?.length ?? 0) === 0;
}

export const designerSignUpSchema = z
  .object({
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        "Password must contain at least one uppercase letter, one lowercase letter, and one number"
      ),
    confirmPassword: z.string(),
    fullName: z
      .string()
      .min(2, "Full name must be at least 2 characters")
      .max(100, "Full name must be less than 100 characters"),
    phone: z.string().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

export const forgotPasswordSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

export const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        "Password must contain at least one uppercase letter, one lowercase letter, and one number"
      ),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

export const changeEmailSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, "Current password is required"),
    newPassword: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        "Password must contain at least one uppercase letter, one lowercase letter, and one number"
      ),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

export const adminUserProfileSchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(2, "Full name must be at least 2 characters")
    .max(100, "Full name must be less than 100 characters"),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email("Please enter a valid email address"),
  phone: z
    .string()
    .trim()
    .max(30, "Phone number must be less than 30 characters")
    .optional(),
});

export const adminUserRoleSchema = z.enum(["clinic", "designer", "admin"]);

export type SignInInput = z.infer<typeof signInSchema>;
export type SignUpInput = z.infer<typeof signUpSchema>;
export type DesignerSignUpInput = z.infer<typeof designerSignUpSchema>;
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;
export type ChangeEmailInput = z.infer<typeof changeEmailSchema>;
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;
export type ClinicProfileInput = z.infer<typeof clinicProfileSchema>;
export type AdminUserProfileInput = z.infer<typeof adminUserProfileSchema>;
export type AdminUserRoleInput = z.infer<typeof adminUserRoleSchema>;
